<template>
  <div class="widget-card-compact">
    <!-- Step header: 2 pasos (rápido) o 3 (con gate de contacto) -->
    <StepHeader
      :step="1"
      :total="showDriverStep ? 3 : 2"
      name="Vehículo"
      title="Selecciona tu vehículo"
      subtitle="Empieza por el año; después elegimos marca y versión."
    />

    <!-- Campos del formulario -->
    <TransitionGroup name="staggered-fade" tag="div" class="widget-fields-compact">
      <!-- Campo Año: select nativo en móvil evita el input de Element Plus y el teclado del sistema -->
      <VehicleSelectField
        id="vehicle-year-select"
        key="year-field"
        label="Año"
        placeholder="Selecciona un año"
        :model-value="selectedYear"
        :options="yearOptions"
        :native="isTouchLike"
        @change="handleYearChange"
      />

      <VehicleSelectField
        id="vehicle-brand-select"
        key="brand-field"
        label="Marca"
        placeholder="Selecciona una marca"
        :model-value="selectedBrandId"
        :options="brandOptions"
        :native="isTouchLike"
        :loading="isLoadingBrands"
        :disabled="!year"
        :filterable="isSelectFilterable"
        @change="emit('brandChange', $event as string)"
      />

      <VehicleSelectField
        v-if="year && brand"
        id="vehicle-type-select"
        key="type-field"
        label="Tipo"
        placeholder="Selecciona un tipo"
        :model-value="selectedTypeId"
        :options="typeOptions"
        :native="isTouchLike"
        :loading="isLoadingTypes"
        :filterable="isSelectFilterable"
        @change="emit('typeChange', $event as string)"
      />

      <VehicleSelectField
        v-if="brand && type"
        id="vehicle-model-select"
        key="model-field"
        label="Versión"
        placeholder="Selecciona una versión"
        full-width
        :model-value="selectedModelId"
        :options="modelOptions"
        :native="isTouchLike"
        :loading="isLoadingModels"
        :filterable="isSelectFilterable"
        @change="emit('modelChange', $event as string)"
      />

      <!-- Campo Código Postal (Autocomplete) -->
      <VehiclePostalField
        v-if="model"
        key="postal-field"
        :postal-code-display="postalCodeDisplay"
        @select="handlePostalCodeSelect"
        @clear="handlePostalCodeClear"
        @invalidate="handlePostalCodeInvalidate"
        @update:postal-code-display="emit('update:postalCodeDisplay', $event)"
      />
    </TransitionGroup>

    <VehicleActions
      :can-continue="canContinue"
      :label="continueButtonLabel"
      @continue="emit('continue')"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { CarBrand, CarModel } from '@/types/quote.types';
import type { PostalCodeOption } from '@/composables/usePostalCode';
import { useSelectFilterable } from '@/composables/useSelectFilterable';
import { useTouchLikeDevice } from '@/composables/useTouchLikeDevice';
import VehicleActions from './vehicleSelection/VehicleActions.vue';
import VehiclePostalField from './vehicleSelection/VehiclePostalField.vue';
import StepHeader from './vehicleSelection/StepHeader.vue';
import VehicleSelectField from './vehicleSelection/VehicleSelectField.vue';
import { toOptions } from './vehicleSelection/vehicleOptions';
import { useVehicleYears } from './vehicleSelection/useVehicleYears';

const { isSelectFilterable } = useSelectFilterable();
const { isTouchLike } = useTouchLikeDevice();
const { yearOptions } = useVehicleYears();

interface Props {
  year: number | null;
  brand: { id: string; name: string } | null;
  type: string | null;
  model: { id: string; name: string } | null;
  postalCode: string;
  postalCodeDisplay: string;
  selectedYear: number | null;
  selectedBrandId: string;
  selectedTypeId: string;
  selectedModelId: string;
  brands: CarBrand[];
  tipos: Array<{ id: string; label: string }>;
  models: CarModel[];
  isLoadingBrands: boolean;
  isLoadingTypes: boolean;
  isLoadingModels: boolean;
  /** Cuando true, el stepper incluye paso Contacto antes de Resultados. */
  showDriverStep?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  showDriverStep: false,
});

const emit = defineEmits<{
  yearChange: [year: number | null];
  brandChange: [brandId: string];
  typeChange: [typeId: string];
  modelChange: [modelId: string];
  continue: [];
  'update:postalCode': [value: string];
  'update:postalCodeDisplay': [value: string];
  'postal-code-select': [item: PostalCodeOption];
}>();

const continueButtonLabel = computed(() =>
  props.showDriverStep ? 'Continuar' : 'Ver Resultados',
);

const canContinue = computed(
  () => Boolean(props.model && props.postalCode && props.postalCode.length >= 4),
);

const brandOptions = computed(() =>
  toOptions(props.brands, (brand) => brand.id, (brand) => brand.name),
);
const typeOptions = computed(() =>
  toOptions(props.tipos, (type) => type.id, (type) => type.label),
);
const modelOptions = computed(() =>
  toOptions(props.models, (model) => model.id, (model) => model.name),
);

const handleYearChange = (value: string | number | null) => {
  emit('yearChange', value ? Number(value) : null);
};

const handlePostalCodeSelect = (item: PostalCodeOption) => {
  emit('update:postalCode', item.code);
  emit('update:postalCodeDisplay', item.displayValue);
  emit('postal-code-select', item);
};

const handlePostalCodeClear = () => {
  emit('update:postalCode', '');
  emit('update:postalCodeDisplay', '');
};

const handlePostalCodeInvalidate = () => {
  emit('update:postalCode', '');
};
</script>

<style scoped>
/* Flat surface: hosts often mount without a background (showBackground: false). */
.widget-card-compact {
  background: var(--ui-surface);
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-panel);
  padding: clamp(16px, 3vw, 28px);
  box-sizing: border-box;
  width: 100%;
  max-width: 680px;
  margin: 0 auto;
  min-height: auto;
  display: flex;
  flex-direction: column;
  position: relative;
}

.widget-compact-mode .widget-card-compact {
  padding: 16px;
}

/* Responsive Grid Layout for Fields */
.widget-fields-compact {
  display: grid;
  grid-template-columns: 1fr;
  gap: 18px;
  margin-bottom: 28px;
}

@media (min-width: 640px) {
  .widget-fields-compact {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 639px) {
  .widget-card-compact {
    padding: 20px 16px;
  }
}

/* Staggered Fade Transition */
.staggered-fade-enter-active,
.staggered-fade-leave-active {
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.staggered-fade-enter-from {
  opacity: 0;
  transform: translateY(10px) scale(0.98);
}

.staggered-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px) scale(0.98);
}
</style>
